import React, { useState, useEffect } from "react"
import { useInView } from "react-intersection-observer"
import { sanitizer, getAssetPath, classNames } from "@commonUtils"
import { directAxiosInstance } from "@lib/api"
import ConfirmDialog from "@components/ConfirmDialog/ConfirmDialog"
import PrimaryButton from "@components/Experiments/vsat/PrimaryButton"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import css from "./ContactUsSection.scss"
import { SUCCESS } from "./constants"

const ContactUsForm = ({ form, setIsAnimationTriggered, onSubmitGA }) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.2,
    })
    useEffect(() => {
        if (inView) {
            setIsAnimationTriggered(true)
        }
    }, [inView])
    const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const [error, setError] = useState("")
    const [inputError, setInputError] = useState("")
    const initialFormData = form?.fields?.reduce((res, field) => {
        res[field.name] = ""
        return res
    }, {})
    const [formData, setFormData] = useState(initialFormData)
    const handleChange = (e) => {
        const { value, name, type, required } = e.target
        setError("")
        if (type === "text" && required && value.length < 2) {
            setInputError((prevErrors) => ({
                ...prevErrors,
                [name]: "Minimum 2 characters required",
            }))
        } else if (type === "tel" && value.length !== 10) {
            setInputError((prevErrors) => ({
                ...prevErrors,
                [name]: "Phone number should be 10 digit number",
            }))
        } else {
            setInputError((prevErrors) => {
                const updatedErrors = { ...prevErrors }
                delete updatedErrors[name]
                return updatedErrors
            })
        }
        setFormData({
            ...formData,
            page: form?.page,
            [name]: name === "number" ? value.replace(/\D/g, "").slice(0, 10) : value,
        })
    }
    const handleSubmit = async (e) => {
        e.preventDefault()
        if (form?.cta?.url) {
            location.href = form?.cta?.url
            return
        } else {
            const newErrors = {}
            form?.fields?.forEach((field) => {
                if (field.required && formData[field.name] === "") {
                    newErrors[field.name] = "This field is required"
                }
            })
            if (Object.keys(newErrors).length > 0) {
                setInputError((prevErrors) => ({
                    ...prevErrors,
                    ...newErrors,
                }))
                return // Prevent form submission if there are errors
            }
            const hasErrors = Object.values(inputError).some((error) => error !== "")
            if (hasErrors) {
                // If there are errors, you can display a message or handle the errors as needed
                setError("Please fix the errors before submitting.")
                return // Stop form submission
            }
            try {
                setError("")
                const response = await directAxiosInstance().post(`/api/user/intent`, formData)
                if (response.status_code === 200) {
                    setFormData(initialFormData)
                    if (typeof onSubmitGA === "function") {
                        onSubmitGA(formData) // Call onSubmitGA with formData
                    }
                    setConfirmDialogIsOpen(true)
                    setTimeout(() => {
                        setConfirmDialogIsOpen(false)
                        setIsVisible(true)
                    }, 5000)
                }
            } catch (err) {
                setError(err?.error?.message || "Something went wrong. Please try again later.")
            }
        }
    }
    const renderField = (field) => {
        const containerStyles = classNames(css.inputContainer, {
            [css.hasContent]: formData[field.name],
        })
        if (field.type === "select") {
            return (
                <div className={`flexColumn ${css.inputWrapper}`}>
                    <label className={containerStyles}>
                        <span htmlFor={field.name} className={`bodyMediumRegular ${css.label}`}>
                            {field.label}
                        </span>
                        <div className={`${css.textField} ${css.selectFieldWrapper}`}>
                            <select
                                id={field.name}
                                className={`bodyMediumRegular ${css.textMain}`}
                                name={field.name}
                                onChange={handleChange}
                                onBlur={handleChange}
                                value={formData[field.name]}
                                onFocus={handleChange}
                                required
                            >
                                <option value="" disabled hidden>
                                    Select an option
                                </option>
                                {field?.options?.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {!field.isDisabled && (
                                <ImageLoader
                                    className={`${css.icon}`}
                                    src={getAssetPath("svgs/icons/caret_down_18.svg")}
                                    alt="dropdown"
                                    height="12"
                                    width="12"
                                />
                            )}
                        </div>
                    </label>
                    {inputError[field.name] && field.required && (
                        <div className={`flex alignCenter`}>
                            <span className="errorColor padding-0-8">{inputError[field.name]}</span>
                        </div>
                    )}
                </div>
            )
        } else if (field.type === "tel") {
            return (
                <div ref={ref} className={`flexColumn ${css.inputWrapper}`}>
                    <label className={containerStyles}>
                        <span
                            htmlFor={field.name}
                            className={`bodyMediumRegular ${css.truncateTo1} ${css.label}`}
                        >
                            {field.label}
                        </span>
                        <div className={css.textField}>
                            <span className={css.prefix}>+91</span>
                            <input
                                id={field.name}
                                type={field.type}
                                className={`bodyMediumRegular ${css.textMain}`}
                                name={field.name}
                                onChange={handleChange}
                                onBlur={handleChange}
                                value={formData[field.name]}
                                onFocus={handleChange}
                                maxLength={10}
                                required
                            />
                        </div>
                    </label>
                    {inputError[field.name] && field.required && (
                        <div className={`flex alignCenter`}>
                            <span className="errorColor padding-0-8">{inputError[field.name]}</span>
                        </div>
                    )}
                </div>
            )
        }
        return (
            <div className={`flexColumn ${css.inputWrapper}`}>
                <label className={containerStyles}>
                    <span
                        htmlFor={field.name}
                        className={`bodyMediumRegular ${css.truncateTo1} ${css.label}`}
                    >
                        {field.label}
                    </span>
                    <div className={css.textField}>
                        <input
                            id={field.name}
                            type={field.type}
                            className={`bodyMediumRegular ${css.textMain}`}
                            name={field.name}
                            onChange={handleChange}
                            onBlur={handleChange}
                            value={formData[field.name]}
                            onFocus={handleChange}
                            required={field.required}
                        />
                    </div>
                </label>
                {inputError[field.name] && field.required && (
                    <div className={`flex alignCenter`}>
                        <span className="errorColor padding-0-8">{inputError[field.name]}</span>
                    </div>
                )}
            </div>
        )
    }

    return (
        <section id="contactUs" className="container-fluid page-container">
            <div>
                <h2
                    className="sectionHeader"
                    dangerouslySetInnerHTML={{
                        __html: sanitizer(form.header),
                    }}
                />
                {form.subHeader && <h3 className="listItemHeader">{form.subHeader}</h3>}
                <form
                    autoComplete={false}
                    onSubmit={handleSubmit}
                    className={`col-11 flex flexColumn padding-16-0 padding-24-0  ${css.form} `}
                >
                    {form?.fields?.map((field) => renderField(field))}
                    <div className="col-6">
                        <PrimaryButton
                            type="submit"
                            theme="coralGradient"
                            labelClass="ctaLabelNormal"
                            label={form.cta.label}
                        />
                    </div>
                </form>
                {!error && (
                    <div className={`flex alignCenter ${isVisible ? css.successMsgShow : "opacity-none"}`}>
                        <img src={getAssetPath("svgs/icons/compeleted_icon.svg")} />
                        <span className="successColor padding-0-8">{form.message}</span>
                    </div>
                )}
                {error && (
                    <div className={`flex alignCenter`}>
                        <span className="errorColor padding-0-8">{error}</span>
                    </div>
                )}

                <ConfirmDialog
                    closeAction={() => setConfirmDialogIsOpen(false)}
                    isDialogBoxOpen={confirmDialogIsOpen}
                >
                    <div className="flexColumn alignCenter">
                        {confirmDialogIsOpen && (
                            <div
                                // eslint-disable-next-line risxss/catch-potential-xss-react
                                dangerouslySetInnerHTML={{
                                    __html: SUCCESS,
                                }}
                            />
                        )}
                        <h2 className="modalTextBold marginTop-16">Thank you!</h2>
                        <p className="modalTextBold">Your request has been registered.</p>
                        <p className={`marginTop-16 detailsTextNormal`}>
                            We will get in touch with you shortly.
                        </p>
                        <div className="marginTop-40 width-100">
                            <PrimaryButton
                                onClick={() => {
                                    setConfirmDialogIsOpen(false)
                                    setIsVisible(true)
                                }}
                                labelClass="ctaLabelNormal"
                                label="Okay!"
                                theme="coralGradient"
                            />
                        </div>
                    </div>
                </ConfirmDialog>
            </div>
        </section>
    )
}
export default ContactUsForm
